import FeatureRecord from './FeatureRecord.js';
import FeatureReference from './FeatureReference.js';
import FeatureReferenceQuery from './FeatureReferenceQuery.js';
import LayerReference from './LayerReference.js';
import FeatureCollection from '../geodata/FeatureCollection.js';
/**
 * @memberof feature-references
 * @property {feature-references.FeatureReferenceQuery} query
 * @property {feature-references.FeatureReference[]} features
 */
class FeatureReferenceCollection {
    /**
     * @param {object} props
     * @param {feature-references.FeatureReferenceQuery} props.query
     * @param {feature-references.FeatureReference[]} props.features
     */
    constructor(props = {}) {
        const features = Array.isArray(props.features) ? props.features : [];
        this.features = features.map(FeatureReference.fromJSON);
        this.query = new FeatureReferenceQuery(props.query);
    }
    /**
     * @property {boolean}
     */
    get isStatic() {
        return this.query.isEmpty;
    }
    /**
     * @property {boolean}
     */
    get isDynamic() {
        return !this.isStatic;
    }
    /**
     * @property {string}
     */
    get hash() {
        return (this.isStatic
            ? this.features.map(ref => ref.key).sort().join('+')
            : queryString(this.query.parameters));
    }
    loadForm() {
        let promise;
        if (this.isDynamic) {
            promise = this.query.parameters.loadForm();
        }
        else {
            promise = this.features.length > 0
                ? FeatureCollection.fromFeatureReferences(this.features).then(FeatureRecord.fromGeoJSONFeatureCollection)
                : Promise.resolve(new FeatureCollection());
        }
        return promise;
    }
    /**
     * Loads the {FeatureCollection} based on the reference collection; either
     * the static list or evaluates the query based on parameters.
     *
     * @return {Promise<FeatureCollection>}
     */
    loadFeatureCollection() {
        let promise;
        if (this.isDynamic) {
            promise = this.query.evaluate();
        }
        else {
            promise = this.features.length > 0
                ? FeatureCollection.fromFeatureReferences(this.features)
                : Promise.resolve(new FeatureCollection());
        }
        return promise;
    }
    /**
     * Loads the list of {FeatureRecord}s based on the collection; either
     * the static list or evaluates the query based on parameters.
     *
     * @return {Promise<feature-references.FeatureRecord[]>}
     */
    load() {
        return this.loadFeatureCollection().then((featureCollection) => {
            const featureRecords = FeatureRecord.fromGeoJSONFeatureCollection(featureCollection);
            return order(this, featureRecords);
        });
    }
    /**
     * @return {object}
     */
    toJSON() {
        const { features, query } = this;
        return this.isStatic ? { features } : { query };
    }
    /**
     * @param {feature-references.FeatureReference} featureReference
     * @param {feature-references.LayerReference} layerReference
     * @return {FeatureReferenceCollection}
     */
    static fromFeatureReferenceAndLayer(featureReference, layerReference) {
        const query = new FeatureReferenceQuery({
            pattern: FeatureReferenceQuery.patterns.spatialQuery.PATTERN_TYPE,
            parameters: new FeatureReferenceQuery.patterns.spatialQuery({
                layer: layerReference,
                constrainBy: featureReference,
            }),
        });
        return new FeatureReferenceCollection({ query });
    }
    /**
     * @param {feature-references.FeatureReference[]} references
     * @return {FeatureReferenceCollection}
     */
    static fromFeatureReferences(references) {
        return new FeatureReferenceCollection({ features: references });
    }
    /**
     * @return {FeatureReferenceCollection}
     */
    static makeInheritingQuery() {
        const query = { pattern: FeatureReferenceQuery.PatternType.INHERIT, parameters: {} };
        return new FeatureReferenceCollection({ query });
    }
    static hashFor(featureReferenceCollections) {
        return (featureReferenceCollections
            .map((collection) => {
            return collection.hash;
        })
            .sort()
            .join('|'));
    }
}
FeatureReferenceCollection.FeatureCollection = FeatureCollection;
FeatureReferenceCollection.FeatureRecord = FeatureRecord;
FeatureReferenceCollection.FeatureReference = FeatureReference;
FeatureReferenceCollection.LayerReference = LayerReference;
FeatureReferenceCollection.Query = FeatureReferenceQuery;
function order(collection, records) {
    let ordered = records;
    if (collection.isStatic) {
        const recordsByKey = records.reduce((memo, record) => {
            memo[record.key] = record;
            return memo;
        }, {});
        ordered = collection.features.map(featureReference => recordsByKey[featureReference.key]);
    }
    return ordered;
}
function queryString(fromParameters) {
    const params = new URLSearchParams();
    Object.keys(fromParameters || {}).sort().forEach((key) => {
        const value = fromParameters[key];
        if (Array.isArray(value)) {
            value.forEach((entry) => params.append(key, entry));
        }
        else {
            params.append(key, (value && value.key) ? value.key : value);
        }
    });
    const stringified = params.toString();
    return (stringified ? `${stringified}` : '').replace(/%3A/g, ':');
}
export default FeatureReferenceCollection;
